import { Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Button } from "../../components/ui/Button";
import { DocMarks, EmptyState, Spinner } from "../../components/ui/misc";
import { Panel } from "../../components/ui/Panel";
import { ApiError } from "../../lib/api/client";
import { can, useMe } from "../../lib/auth";
import { formatDay } from "../../lib/format";
import { useItem } from "../../lib/queries";
import { ITEMS_TITLE } from "./ClientItems";
import { ItemDialog } from "./ItemDialog";

const TAB = { device: "/clients/$clientId/devices", network: "/clients/$clientId/network" } as const;
const NONE = <span className="text-ink-3">Not recorded</span>;

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 border-t border-line/60 py-2 first:border-0">
      <dt className="text-ink-3">{label}</dt>
      <dd className="text-ink">{children}</dd>
    </div>
  );
}

/**
 * One device or network item: Gorelo's fields, the location and notes kept in Docs, and the documents that cover
 * it. It is made and removed in Gorelo; Edit changes only what Docs owns (Gorelo sync §6.2).
 */
export function ItemPage({ clientId, itemId }: { clientId: string; itemId: string }) {
  const me = useMe();
  const { data: item, error, isLoading } = useItem(itemId);
  const [editing, setEditing] = useState(false);

  if (isLoading) return <Spinner label="Loading…" />;
  if (error instanceof ApiError && error.status === 404)
    return <EmptyState title="This device or network item does not exist or was removed in Gorelo" />;
  if (error || !item) return <EmptyState title="This item could not be loaded" body={error?.message} />;

  return (
    <div className="grid gap-4">
      <Link
        to={TAB[item.category]}
        params={{ clientId }}
        className="flex items-center gap-1 text-sm text-green-ink hover:underline"
      >
        <ArrowLeft size={13} aria-hidden="true" /> {ITEMS_TITLE[item.category]}
      </Link>
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel aria-label="Details">
          <div className="flex flex-wrap items-start gap-2">
            <div>
              <div className="text-xs text-ink-3">
                {item.kind} · {ITEMS_TITLE[item.category]}
              </div>
              <h2 className="font-semibold text-2xl text-ink">{item.name}</h2>
            </div>
            {item.source !== null && can(me, "edit") ? (
              <Button size="sm" className="ml-auto" onClick={() => setEditing(true)}>
                Edit
              </Button>
            ) : null}
          </div>
          {item.source === null ? (
            <p className="text-sm text-ink-3">Typed in Docs before the Gorelo sync. It cannot be changed.</p>
          ) : null}
          <dl className="text-sm">
            <Row label="Kind">{item.kind}</Row>
            <Row label="Manufacturer">{item.manufacturer || NONE}</Row>
            <Row label="Model">{item.model || NONE}</Row>
            <Row label="Serial number">
              {item.serial ? <span className="font-mono text-xs">{item.serial}</span> : NONE}
            </Row>
            <Row label="Warranty start">{formatDay(item.warrantyStart) || NONE}</Row>
            <Row label="Warranty end">{formatDay(item.warrantyEnd) || NONE}</Row>
            <Row label="Site">{item.siteName || <span className="text-ink-3">No site</span>}</Row>
            <Row label="Location">{item.location || NONE}</Row>
            <Row label="Notes">
              {item.notes ? (
                <span className="whitespace-pre-line">{item.notes}</span>
              ) : (
                <span className="text-ink-3">None</span>
              )}
            </Row>
          </dl>
        </Panel>
        <Panel aria-label="In documents" title={`In documents (${item.documents.length})`}>
          {item.documents.length === 0 ? (
            <p className="text-sm text-ink-3">
              No document covers this item yet. Link it from a document's Details panel, under "Covers".
            </p>
          ) : (
            <ul className="grid gap-1.5">
              {item.documents.map((d) => (
                <li key={d.id}>
                  <Link
                    to="/d/$ref"
                    params={{ ref: d.id }}
                    className="flex items-center justify-between gap-2 text-ink hover:text-green-ink"
                  >
                    <span>{d.title}</span>
                    <span className="flex shrink-0 items-center gap-2">
                      <DocMarks code={d.code} kind={d.kind} />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
      <ItemDialog clientId={clientId} item={item} open={editing} onClose={() => setEditing(false)} />
    </div>
  );
}
